import React, { useEffect, useId, useState } from 'react';
import Modal from './Modal';
import { FamilyMemberCreate } from '../api/families';
import { formatApiError } from '../utils/errorUtils';

export interface MemberFormModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  submitLabel: string;
  initial: FamilyMemberCreate;
  onSubmit: (data: FamilyMemberCreate) => Promise<void>;
}

const emptyMember: FamilyMemberCreate = {
  name: '',
  date_of_birth: '',
  gender: undefined,
  member_relationship: '',
  notes: '',
};

const MemberFormModal: React.FC<MemberFormModalProps> = ({
  open,
  onClose,
  title,
  submitLabel,
  initial,
  onSubmit,
}) => {
  const formId = useId();
  const [data, setData] = useState<FamilyMemberCreate>(emptyMember);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setData({
        name: initial.name || '',
        date_of_birth: initial.date_of_birth || '',
        gender: initial.gender,
        member_relationship: initial.member_relationship || '',
        notes: initial.notes || '',
      });
      setError('');
    }
  }, [open, initial]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload: FamilyMemberCreate = {
        name: data.name.trim(),
        date_of_birth: data.date_of_birth || undefined,
        gender: data.gender,
        member_relationship: data.member_relationship || undefined,
        notes: data.notes || undefined,
      };
      await onSubmit(payload);
      onClose();
    } catch (err: any) {
      setError(formatApiError(err.response?.data?.detail) || 'Eroare');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Anulează
          </button>
          <button type="submit" form={formId} className="btn btn-primary" disabled={saving}>
            {saving ? 'Se salvează…' : submitLabel}
          </button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        {error && <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)' }}>{error}</div>}
        <div className="form-group">
          <label className="form-label">Nume</label>
          <input
            className="form-input"
            type="text"
            value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            required
            autoFocus
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Data nașterii</label>
            <input
              className="form-input"
              type="date"
              value={data.date_of_birth || ''}
              onChange={(e) => setData({ ...data, date_of_birth: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Gen</label>
            <select
              className="form-select"
              value={data.gender || ''}
              onChange={(e) =>
                setData({
                  ...data,
                  gender: (e.target.value || undefined) as FamilyMemberCreate['gender'],
                })
              }
            >
              <option value="">Selectează</option>
              <option value="male">Masculin</option>
              <option value="female">Feminin</option>
              <option value="other">Altul</option>
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Relație</label>
          <input
            className="form-input"
            type="text"
            placeholder="ex: soț, soție, copil"
            value={data.member_relationship || ''}
            onChange={(e) => setData({ ...data, member_relationship: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Note</label>
          <textarea
            className="form-textarea"
            value={data.notes || ''}
            onChange={(e) => setData({ ...data, notes: e.target.value })}
          />
        </div>
      </form>
    </Modal>
  );
};

export default MemberFormModal;
